import {Box, CircularProgress, InputAdornment, Skeleton} from '@mui/material';
import {AppDropDownMenu, AppDropDownMenuOptions, SelectChipView} from 'components/menus';
import {useDebounceEffect, useGetAllSprints, useSprintsRedux} from 'hooks';
import {sortBy} from 'lodash';
import {SprintResponseModel, SprintStatus} from 'models/sprint.model';
import {useItemDataState} from 'providers';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import {useParams} from 'react-router-dom';
import SprintTitleRenderer from './SprintTitleRenderer';
import {AddUpdateSprintDialog} from './dialog/AddUpdateSprintDialog';

export const SprintsDropDownMenu: React.FC = () => {
    const {clearBoardItemData} = useItemDataState();
    const [searchFieldValue, setSearchFieldValue] = useState<string>('');
    const [openSprintDialog, setOpenSprintDialog] = useState<boolean>(false);
    const {selectedSprint, setSelectedSprintInRedux} = useSprintsRedux();
    const [sprintToUpdate, setSprintToUpdate] = useState<SprintResponseModel | undefined>(undefined);
    const {boardId} = useParams();
    const {getAllSprints, getAllSprintsLoading, getAllSprintsResponse} = useGetAllSprints();
    const handleSearchField = (fieldValue: string) => {
        setSearchFieldValue(fieldValue);
    };
    const didSetInitialSprint = useRef(false);

    const getAllSprintHandler = useCallback(() => {
        didSetInitialSprint.current = false;
        if (boardId) {
            getAllSprints({
                variables: {
                    boardId: boardId,
                    search: searchFieldValue.toLocaleLowerCase(),
                    first: 50,
                },
            });
        }
    }, [boardId]);

    useEffect(() => {
        setSearchFieldValue('');
        setSelectedSprintInRedux(null);
        didSetInitialSprint.current = false; // Reset initial sprint flag
    }, [boardId]);

    useDebounceEffect(
        () => {
            if (boardId) {
                getAllSprints({
                    variables: {
                        boardId: boardId,
                        search: searchFieldValue.toLocaleLowerCase(),
                        first: 50,
                    },
                });
            }
        },
        [searchFieldValue, boardId],
        1000,
    );

    const handleChipValue = useCallback((status: SprintStatus): SelectChipView => {
        switch (status) {
            case SprintStatus.Current:
                return {color: 'primary', label: 'Current'};
            case SprintStatus.Future:
                return {color: 'secondary', label: 'Future'};
            case SprintStatus.Past:
                return {color: 'error', label: 'Past'};
            default:
                return {color: 'error', label: 'Closed'};
        }
    }, []);

    useEffect(() => {
        if (getAllSprintsResponse && !getAllSprintsLoading) {
            const allSprint: SprintResponseModel = {
                title: 'All',
                id: '',
                sprintStatus: SprintStatus.All,
                startData: '',
                endData: '',
                boardId: '',
                isPinned: true,
                isDefault: true,
            };
            if (!selectedSprint && getAllSprintsResponse.nodes?.length === 0) {
                setSelectedSprintInRedux(allSprint);
            }
            // Set initial sprint only once (and only if none is selected yet)
            if (!didSetInitialSprint.current) {
                const currentSprint = getAllSprintsResponse?.nodes.find(x => x.sprintStatus === SprintStatus.Current);
                if (currentSprint) {
                    setSelectedSprintInRedux(currentSprint);
                } else {
                    setSelectedSprintInRedux(allSprint);
                }
                didSetInitialSprint.current = true;
            }
        }
    }, [getAllSprintsResponse, getAllSprintsLoading]);

    const sprintOptions = useMemo<AppDropDownMenuOptions[]>(() => {
        const baseOption: AppDropDownMenuOptions = {
            label: 'All',
            value: SprintStatus.All.toString(),
            isPinned: true,
            isDeletable: false,
            isEditable: false,
        };

        if (!getAllSprintsResponse) {
            return [baseOption];
        }

        const sprintData: AppDropDownMenuOptions[] = getAllSprintsResponse.nodes.map(x => ({
            label: !x.isDefault ? <SprintTitleRenderer sprint={x} /> : x.title,
            value: x.id ?? '',
            chip: !x.isDefault ? handleChipValue(x.sprintStatus) : undefined,
            isPinned: x.isPinned,
            isEditable: !x.isDefault,
            isDeletable: !x.isDefault,
        }));

        // Sort sprints: isDefault items first, then isPinned items, then the rest
        const sortedSprintData = sortBy(sprintData, [
            // Sort by isDefault (false values come after true values)
            item => {
                const sprint = getAllSprintsResponse?.nodes.find(x => x.id === item.value);
                return sprint?.isDefault ? 0 : 1;
            },
            // Then sort by isPinned (false values come after true values)
            item => (item.isPinned ? 0 : 1),
        ]);
        return [baseOption, ...sortedSprintData];
    }, [getAllSprintsResponse, selectedSprint]);

    const handleOpenSprintDialog = () => {
        setOpenSprintDialog(true);
    };
    const handleCloseSprintDialog = (id?: string) => {
        setOpenSprintDialog(false);
        setSprintToUpdate(undefined);
        setSearchFieldValue('');
        if (id) {
            getAllSprintHandler(); // fetch fresh data
        }
    };

    const handleSelectSprint = (selectedVal: AppDropDownMenuOptions) => {
        if (selectedSprint?.id === selectedVal.value) return; // Avoid unnecessary updates
        if (SprintStatus.All.toString() === selectedVal.value) {
            setSelectedSprintInRedux({
                title: 'All',
                id: '',
                sprintStatus: SprintStatus.All,
                startData: '',
                endData: '',
                boardId: '',
                isPinned: true,
                isDefault: true,
            });
        } else if (selectedVal.value === '') setSelectedSprintInRedux(null);
        setSearchFieldValue('');
        const willSprint = getAllSprintsResponse?.nodes.find(x => x.id === selectedVal.value);
        if (willSprint) {
            clearBoardItemData();
            setSelectedSprintInRedux(willSprint);
        }
    };
    const handleSprintToUpdate = useCallback(
        (value: string) => {
            setSprintToUpdate(getAllSprintsResponse?.nodes.find(x => x.id === value));
            handleOpenSprintDialog();
        },
        [getAllSprintsResponse],
    );
    const handleNext = () => {
        if (boardId) {
            getAllSprints({
                variables: {
                    boardId: boardId,
                    search: searchFieldValue,
                    after: getAllSprintsResponse?.pageInfo.endCursor,
                    first: 50,
                },
            });
        }
    };

    // Pass refresh handler to dialog
    const handleAddOrUpdateSprint = () => {
        setSearchFieldValue('');
        getAllSprintHandler(); // fetch fresh data
    };
    const hasSprints =
        getAllSprintsResponse &&
        getAllSprintsResponse.nodes.length > 0 &&
        getAllSprintsResponse.nodes.find(x => x === selectedSprint);
    // Only hide if there are sprints but neither a selected sprint nor 'All' is selected
    if (hasSprints && !selectedSprint && searchFieldValue !== '') return null;
    if (!selectedSprint) {
        return <></>;
    }
    return (
        <>
            <div style={{display: 'flex', alignItems: 'center', paddingRight: '20px'}}>
                <AppDropDownMenu
                    selectedItemValue={selectedSprint?.id}
                    options={sprintOptions}
                    searchFieldTitle="Search sprints"
                    addMenuItemTitle="Add new sprint"
                    onClose={() => setSearchFieldValue('')}
                    paperWidth="400px"
                    renderOptions={value => (
                        <InfiniteScroll
                            scrollableTarget="spritnScrollableDiv"
                            dataLength={getAllSprintsResponse?.nodes.length ?? 0}
                            next={handleNext}
                            hasMore={getAllSprintsResponse?.pageInfo.hasNextPage ?? false}
                            loader={
                                <Skeleton
                                    style={{borderRadius: '10px', margin: '10px'}}
                                    variant="rectangular"
                                    height={30}
                                />
                            }
                        >
                            {value}
                        </InfiniteScroll>
                    )}
                    handleOptionEdit={handleSprintToUpdate}
                    handleOptionSelect={handleSelectSprint}
                    onClickAddMenuItemHandler={handleOpenSprintDialog}
                    searchFieldProps={{
                        value: searchFieldValue,
                        InputProps: {
                            endAdornment: getAllSprintsLoading ? (
                                <InputAdornment position="end">
                                    <CircularProgress size={20} />
                                </InputAdornment>
                            ) : null,
                        },
                        onChange: e => handleSearchField(e.target.value),
                    }}
                    renderElement={handleOpen => (
                        <Box
                            sx={{
                                display: 'flex',
                                border: '1px solid',
                                borderRadius: '5px',
                                minWidth: '150px',
                                paddingTop: '5px',
                                paddingBottom: '5px',
                                paddingLeft: '10px',
                                paddingRight: '10px',
                                borderColor: 'grey.300',
                                alignItems: 'center',
                                width: '100%',
                            }}
                            onClick={handleOpen}
                        >
                            <SprintTitleRenderer sprint={selectedSprint} />
                        </Box>
                    )}
                />
            </div>
            {openSprintDialog && (
                <AddUpdateSprintDialog
                    open={openSprintDialog}
                    sprintToUpdate={sprintToUpdate}
                    onCloseDialog={handleCloseSprintDialog}
                    handleUpdateSprint={handleAddOrUpdateSprint}
                    handleAddSprint={handleAddOrUpdateSprint}
                />
            )}
        </>
    );
};
